import { LeftOutlined, PlusOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button, message, Spin } from "antd";
import { useState } from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import type { AppRole } from "@/shared/config/navigation";
import { canCreateClient } from "@/shared/config/permissions";
import { parseApiError } from "@/shared/utils/error";
import { ClientsFilters } from "../components/ClientsFilters";
import { ClientsTable } from "../components/ClientsTable";
import { getClients } from "../services/clients.backend";

type ClientsLocationState = {
	fromDashboard?: boolean;
	search?: string;
};

export function ClientsPage() {
	const navigate = useNavigate();
	const location = useLocation();
	const { role } = useOutletContext<{ role: AppRole }>();
	const locationState = location.state as ClientsLocationState | null;
	const canCreateClients = canCreateClient(role);

	const fromDashboard = locationState?.fromDashboard === true;

	// Filters states
	const [searchQuery, setSearchQuery] = useState(locationState?.search || "");
	const [selectedBuilding, setSelectedBuilding] = useState("Barcha bino");
	const [selectedManager, setSelectedManager] = useState("Barcha menejer");
	const [selectedStatus, setSelectedStatus] = useState("Barcha holat");

	const buildingOptions = ["Barcha bino", "Dream", "Yunusobod", "Unicorp City"];
	const managerOptions = ["Barcha menejer", "Sardor T.", "Kamol X.", "Aziza R."];
	const statusOptions = ["Barcha holat", "Faol", "Bekor qilingan"];

	const { data, isLoading, isError, error } = useQuery({
		queryKey: ["clients"],
		queryFn: () => getClients({ limit: 100 }),
	});

	if (isError) {
		message.error(parseApiError(error));
	}

	const clients = data?.items ?? [];

	// Filter clients list
	const filteredClients = clients.filter((client) => {
		// Search query filter
		const fullName = [client.lastName, client.firstName, client.patronymic]
			.filter(Boolean)
			.join(" ")
			.toLowerCase();
		const matchesSearch =
			fullName.includes(searchQuery.toLowerCase()) || client.phone.includes(searchQuery);

		// Building filter
		const matchesBuilding =
			selectedBuilding === "Barcha bino" || client.buildingName === selectedBuilding;

		// Manager filter
		const matchesManager =
			selectedManager === "Barcha menejer" || client.managerName === selectedManager;

		// Status filter
		let matchesStatus = true;
		if (selectedStatus === "Faol") {
			matchesStatus = client.status === "faol" || client.status === "active";
		} else if (selectedStatus === "Bekor qilingan") {
			matchesStatus =
				client.status === "bekor" ||
				client.status === "bekorqilingan" ||
				client.status === "disabled";
		}

		return matchesSearch && matchesBuilding && matchesManager && matchesStatus;
	});

	return (
		<div className="crm-clients">
			<div className="crm-page-head">
				<div>
					<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
						{fromDashboard && (
							<Button
								type="text"
								icon={<LeftOutlined />}
								onClick={() => navigate(-1)}
								style={{ color: "var(--crm-text-secondary)" }}
							/>
						)}
						<h1>Mijozlar</h1>
					</div>
					<p>
						{fromDashboard ? (
							<span style={{ color: "var(--crm-text-secondary)" }}>Bosh sahifa / </span>
						) : null}
						Tashkilotning barcha mijozlari · {data?.meta.total ?? filteredClients.length} ta
					</p>
				</div>
				{canCreateClients ? (
					<Button
						className="crm-primary-btn"
						icon={<PlusOutlined />}
						onClick={() => navigate("/clients/new")}
					>
						Yangi mijoz
					</Button>
				) : null}
			</div>

			<ClientsFilters
				searchQuery={searchQuery}
				setSearchQuery={setSearchQuery}
				selectedBuilding={selectedBuilding}
				setSelectedBuilding={setSelectedBuilding}
				selectedManager={selectedManager}
				setSelectedManager={setSelectedManager}
				selectedStatus={selectedStatus}
				setSelectedStatus={setSelectedStatus}
				buildingOptions={buildingOptions}
				managerOptions={managerOptions}
				statusOptions={statusOptions}
			/>

			{isLoading ? (
				<div style={{ padding: 60, textAlign: "center" }}>
					<Spin size="large" />
				</div>
			) : (
				<ClientsTable
					clients={filteredClients}
					onOpenClient={(client) => navigate(`/clients/${client.id}`)}
				/>
			)}
		</div>
	);
}
export default ClientsPage;
